<template>
  <!-- En-tête de la page -->
  <div class="page-header">
    <h2>Gestion des Utilisateurs</h2>
    <button @click="openCreateModal" class="btn btn-primary">+ Nouvel utilisateur</button>
  </div>
  <!-- Messages d'info -->
  <InfoBox
    v-if="infoMessage"
    v-model:open="infoOpen"
    :type="infoType"
    @close="infoOpen = false; infoMessage = ''"
  >
    {{ infoMessage }}
  </InfoBox>
  <!-- Filtres de recherche -->
  <div class="filters form-group">
    <div class="btn-group">
      <div class="filters-group">
        <label class="field__label">Nom / Prénom</label>
        <input v-model="filters.name" type="text" placeholder="Rechercher..." class="input" />
      </div>
      <div class="filters-group">
        <label class="field__label">Email</label>
        <input v-model="filters.email" type="text" placeholder="Rechercher..." class="input" />
      </div>
      <div class="filters-group">
        <label class="field__label">Téléphone</label>
        <input v-model="filters.phone" type="text" placeholder="Rechercher..." class="input" />
      </div>
      <div class="filters-group">
        <label class="field__label">Rôle</label>
        <select v-model="filters.role" class="input">
          <option value="">-- Tous les rôles --</option>
          <option value="ROLE_ADMIN">Administrateur</option>
          <option value="ROLE_USER">Utilisateur</option>
          <option value="ROLE_SELLER">Vendeur</option>
          <option value="ROLE_DRIVER">Chauffeur</option>
        </select>
      </div>
      <div class="filters-group">
        <label class="field__label">Statut</label>
        <select v-model="filters.verified" class="input">
          <option value="">-- Tous --</option>
          <option value="true">Vérifié</option>
          <option value="false">Non vérifié</option>
        </select>
      </div>
    </div>
    <div class="btn-group">
      <span class="badge badge-info">{{ filteredUsers.length }} utilisateur(s) trouvé(s) sur {{
        allUsers.length }}
      </span>
      <button @click="resetFilters" class="btn btn-secondary">Réinitialiser</button>
    </div>
  </div>
  <!-- Table des utilisateurs -->
  <table v-if="!loading && allUsers.length > 0" class="table">
    <thead>
      <tr>
        <th @click="sortBy('lastName')" class="th--sortable">Nom <span class="sort-icon">{{ getSortIcon('lastName') }}</span></th>
        <th @click="sortBy('firstName')" class="th--sortable">Prénom <span class="sort-icon">{{ getSortIcon('firstName') }}</span></th>
        <th @click="sortBy('email')" class="th--sortable">Email <span class="sort-icon">{{ getSortIcon('email') }}</span></th>
        <th>Téléphone</th>
        <th>Rôles</th>
        <th @click="sortBy('isVerified')" class="th--sortable">Statut <span class="sort-icon">{{ getSortIcon('isVerified') }}</span></th>
        <th>Actions</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="user in visibleUsers" :key="user.id" class="row">
        <td><strong>{{ user.lastName || '-'}}</strong></td>
        <td>{{ user.firstName || '-'}}</td>
        <td><a :href="`mailto:${user.email}`" class="link">{{ user.email }}</a></td>
        <td>
          <span v-if="user.phone">{{ user.phone }}</span>
          <span v-else class="muted">-</span>
        </td>
        <td>
          <div class="roles">
            <span v-for="role in user.roles" :key="role" :class="['badge', getRoleClass(role)]">
              {{ getRoleLabel(role) }}
            </span>
          </div>
        </td>
        <td>
          <span :class="['badge', user.isVerified ? 'badge-verified' : 'badge-unverified']">
            <!--{{ user.isVerified ? '✓ Vérifié' : user.verificationCode }}-->
            {{ user.isVerified ? '✓ Vérifié' : '⚠ Non vérifié' }}
          </span>
        </td>
        <td>
          <button @click="openEditModal(user)" class="icon-btn" title="Modifier">✏️</button>
          <button @click="openDeleteModal(user)" class="icon-btn danger" title="Supprimer">🗑</button>
        </td>
      </tr>
    </tbody>
  </table>
  <!-- Empty state -->
  <EmptyState
    v-if="!loading && filteredUsers.length === 0 && allUsers.length > 0"
    :is-empty="true"
    emptyMessage="Aucun utilisateur ne correspond aux filtres"
  />
  <!-- Bouton Charger plus -->
  <div v-if="!loading && visibleUsers.length > 0 && visibleCount < filteredUsers.length" class="load-more">
    <button @click="loadMore" class="btn btn--primary">Charger plus</button>
  </div>
  <!-- Empty state when no users -->
  <EmptyState
    v-if="!loading && allUsers.length === 0"
    :is-empty="true"
    emptyMessage="Aucun utilisateur pour le moment"
  />
</template>

<script setup>
import { config } from '@/config/env.js'
import { ref, onMounted, computed } from 'vue'
import { useToast } from 'vue-toast-notification'
import { useModalStore } from '@/stores/modalStore'
import { useCountriesStore } from '@/stores/countriesStore'
import { storeToRefs } from 'pinia'
import FormFieldsUser from '@/components/FormFields/FormFieldsUser.vue'
import InfoBox from '@/components/InfoBox.vue'
import EmptyState from '@/components/EmptyState.vue'

const emit = defineEmits(['loading'])
const toast = useToast()
const modalStore = useModalStore()
const infoMessage = ref('')
const infoType = ref('info')
const infoOpen = ref(false)
const countriesStore = useCountriesStore()
const { countries } = storeToRefs(countriesStore)

const allUsers = ref([])
const loading = ref(false)
const visibleCount = ref(10)
const ITEMS_STEP = 10

const sortKey = ref('lastName')
const sortOrder = ref('asc')

const filters = ref({
  name: '',
  email: '',
  phone: '',
  role: '',
  verified: ''
})

const resetFilters = () => {
  filters.value = {
    name: '',
    email: '',
    phone: '',
    role: '',
    verified: ''
  }
  visibleCount.value = ITEMS_STEP
}

const filteredUsers = computed(() => {
  return allUsers.value.filter(user => {
    if (filters.value.name) {
      const searchTerm = filters.value.name.toLowerCase()
      const fullName = `${user.firstName} ${user.lastName}`.toLowerCase()
      if (!fullName.includes(searchTerm)) return false
    }

    if (filters.value.email) {
      const searchTerm = filters.value.email.toLowerCase()
      const email = user.email?.toLowerCase() || ''
      if (!email.includes(searchTerm)) return false
    }

    if (filters.value.phone) {
      const searchTerm = filters.value.phone.toLowerCase()
      const phone = user.phone?.toLowerCase() || ''
      if (!phone.includes(searchTerm)) return false
    }

    if (filters.value.role) {
      if (!user.roles || !user.roles.includes(filters.value.role)) return false
    }

    if (filters.value.verified !== '') {
      const isVerified = filters.value.verified === 'true'
      if (user.isVerified !== isVerified) return false
    }

    return true
  })
})

const sortedUsers = computed(() => {
  const users = [...filteredUsers.value]
  users.sort((a, b) => {
    let aVal, bVal
    switch (sortKey.value) {
      case 'firstName': aVal = a.firstName || ''; bVal = b.firstName || ''; break
      case 'lastName': aVal = a.lastName || ''; bVal = b.lastName || ''; break
      case 'email': aVal = a.email || ''; bVal = b.email || ''; break
      case 'isVerified': aVal = a.isVerified ? 1 : 0; bVal = b.isVerified ? 1 : 0; break
      default: return 0
    }
    if (aVal < bVal) return sortOrder.value === 'asc' ? -1 : 1
    if (aVal > bVal) return sortOrder.value === 'asc' ? 1 : -1
    return 0
  })
  return users
})

const visibleUsers = computed(() => sortedUsers.value.slice(0, visibleCount.value))

const sortBy = (key) => {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortOrder.value = 'asc'
  }
}

const getSortIcon = (key) => sortKey.value !== key ? '⇅' : (sortOrder.value === 'asc' ? '↑' : '↓')
const loadMore = () => { if (visibleCount.value < sortedUsers.value.length) visibleCount.value += ITEMS_STEP }

const getRoleClass = (role) => {
  switch (role) {
    case 'ROLE_ADMIN': return 'badge-admin'
    case 'ROLE_DRIVER': return 'badge-driver'
    case 'ROLE_USER': return 'badge-user'
    case 'ROLE_SELLER': return 'badge-seller'
    default: return 'role-default'
  }
}

const getRoleLabel = (role) => {
  switch (role) {
    case 'ROLE_ADMIN': return 'Admin'
    case 'ROLE_DRIVER': return 'Chauffeur'
    case 'ROLE_SELLER': return 'Vendeur'
    case 'ROLE_USER': return 'Utilisateur'
    default: return role
  }
}

onMounted(async () => {
  await countriesStore.fetchCountries()
  await loadUsers()
})

const loadUsers = async () => {
  loading.value = true
  emit('loading', true)

  try {
    const res = await fetch(config.apiUrl + '/user/admin', { credentials: 'include' })

    if (res.ok) {
      const data = await res.json()
      allUsers.value = data || []
      visibleCount.value = ITEMS_STEP
    } else {
      showInfo('error', 'Impossible de charger les utilisateurs')
      toast.error('Erreur lors du chargement des utilisateurs')
    }
  } catch (err) {
    showInfo('error', err?.message || 'Erreur de connexion')
    toast.error('Erreur de connexion')
  } finally {
    loading.value = false
    emit('loading', false)
  }
}

// 🎯 CRÉATION
const openCreateModal = async () => {
  const { result } = modalStore.openComponent(
    FormFieldsUser,
    {
      formData: {
        firstName: '',
        lastName: '',
        email: '',
        country: '',
        phone: '',
        password: '',
        roles: ['ROLE_USER'],
        isVerified: false
      },
      countries: countries.value,
      isEdit: false
    },
    { title: 'Créer un utilisateur', maxWidth: '600px', kind: 'default' }
  )
  const res = await result
  if (res.action === 'submit') await handleSaveUser(res.data, false)
}

// 🎯 ÉDITION
const openEditModal = async (user) => {
  const { result } = modalStore.openComponent(
    FormFieldsUser,
    {
      formData: {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        country: user.country?.id,
        phone: user.phone || '',
        password: '',
        roles: [...user.roles],
        isVerified: user.isVerified,
        verificationCode: user.verificationCode
      },
      countries: countries.value,
      isEdit: true
    },
    { title: 'Modifier l\'utilisateur', maxWidth: '600px', kind: 'default' }
  )
  const res = await result
  if (res.action === 'submit') await handleSaveUser(res.data, true, user.id)
}

const handleSaveUser = async (formData, isEdit, id = null) => {
  if (formData.roles.length === 0) {
    showInfo('error', 'Au moins un rôle doit être sélectionné')
    toast.error('Au moins un rôle doit être sélectionné')
    return
  }

  loading.value = true
  emit('loading', true)

  try {
    const url = isEdit ? `${config.apiUrl}/user/${id}` : config.apiUrl + '/user/create'
    const method = isEdit ? 'PUT' : 'POST'

    const payload = {
      firstName: formData.firstName || null,
      lastName: formData.lastName || null,
      email: formData.email || null,
      countryId: formData.country || null,
      phone: formData.phone || null,
      roles: formData.roles,
      isVerified: formData.isVerified
    }

    if (formData.password) {
      payload.password = formData.password
    }

    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    })

    if (res.ok) {
      const data = await res.json()
      console.log('Réponse API:', data)
      await loadUsers()
      toast.success(isEdit ? 'Utilisateur modifié' : 'Utilisateur créé')
      showInfo('success', isEdit ? 'Utilisateur modifié avec succès' : 'Utilisateur créé avec succès')
    } else {
      const errorData = await res.json().catch(() => ({}))
      showInfo('error', errorData.message || 'Erreur lors de l\'opération')
      toast.error(errorData.message || 'Erreur lors de l\'opération')
      console.error('Erreur API:', errorData)
    }
  } catch (err) {
    showInfo('error', err?.message || 'Erreur de connexion')
    toast.error('Erreur de connexion')
    console.error(err)
  } finally {
    loading.value = false
    emit('loading', false)
  }
}

// 🎯 SUPPRESSION
const openDeleteModal = async (user) => {
  const { result } = modalStore.open({
    kind: 'confirm',
    title: 'Confirmer la suppression',
    message: `Êtes-vous sûr de vouloir supprimer cet utilisateur ?

Nom: ${user.firstName} ${user.lastName}
Email: ${user.email}
Téléphone: ${user.phone || 'N/A'}

⚠️ Cette action est irréversible.`,
    confirmText: 'Supprimer',
    cancelText: 'Annuler'
  })
  const res = await result
  if (res.action === 'confirm') await handleDeleteUser(user.id)
}

const handleDeleteUser = async (id) => {
  loading.value = true
  emit('loading', true)

  try {
    const res = await fetch(`${config.apiUrl}/user/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    })

    if (res.ok) {
      await loadUsers()
      toast.success('Utilisateur supprimé')
      showInfo('success', 'Utilisateur supprimé avec succès')
    } else {
      showInfo('error', 'Erreur lors de la suppression')
      toast.error('Erreur lors de la suppression')
      console.error('Erreur API:', await res.text())
    }
  } catch (err) {
    showInfo('error', err?.message || 'Erreur de connexion')
    toast.error('Erreur de connexion')
    console.error(err.message)
  } finally {
    loading.value = false
    emit('loading', false)
  }
}
const showInfo = (type = 'info', message) => {
  infoMessage.value = message
  infoType.value = type
  infoOpen.value = true
}
</script>
